import React from 'react';
import { POSTURE_THEME } from './lib/theme';

interface MetricGaugeProps {
  label?: string;
  value: number;
  formattedValue: string;
  unit: string;
  severity: 0 | 1 | 2 | 3;
  ideal?: number;
  range?: [number, number]; // min, max
  type?: 'linear' | 'circular';
  leftLabel?: string;
  rightLabel?: string;
  mirrored?: boolean;
  density?: 'comfortable' | 'compact' | 'dense';
  colors?: {
    level0?: string;
    level1?: string;
    level2?: string;
    level3?: string;
  };
}

const MetricGauge: React.FC<MetricGaugeProps> = ({
  label,
  value,
  formattedValue,
  unit,
  severity,
  ideal = 0,
  range = [-20, 20],
  type = 'linear',
  leftLabel = 'L',
  rightLabel = 'R',
  mirrored = false,
  density = 'comfortable',
  colors
}) => {
  const severityColors = {
    0: colors?.level0 || '#4ade80', // Normal - green
    1: colors?.level1 || '#fb923c', // Mild - orange
    2: colors?.level2 || '#ef4444', // Moderate - red
    3: colors?.level3 || '#dc2626'  // Severe - dark red
  };

  const currentColor = severityColors[severity];
  
  if (type === 'linear') {
    const min = range[0];
    const max = range[1];
    const width = max - min;
    
    // Calculate base percentage (0 to 100)
    let percentage = Math.min(100, Math.max(0, ((value - min) / width) * 100));
    let idealPercentage = ((ideal - min) / width) * 100;

    // If mirrored, flip the visual representation
    if (mirrored) {
        percentage = 100 - percentage;
        idealPercentage = 100 - idealPercentage;
    }

    // Determine active side for highlighting
    // Standard: Value < Ideal -> Left Label Active.
    // Mirrored: Visually flipped.
    // If Mirrored:
    //    Value < Ideal -> Active is Right Label (because bar goes Right).
    //    Value > Ideal -> Active is Left Label (because bar goes Left).
    
    const isLeftActive = mirrored ? value > ideal : value < ideal;
    const isRightActive = mirrored ? value < ideal : value > ideal;

    const isCompact = density === 'compact';
    const isDense = density === 'dense';

    const activeStyle = {
        color: currentColor,
        fontSize: isDense ? '10px' : isCompact ? '11px' : '13px',
        fontWeight: 'bold' as const
    };

    const inactiveLabelClass = isDense 
      ? 'text-muted-foreground text-[9px] font-normal' 
      : isCompact 
        ? 'text-muted-foreground text-[10px] font-normal' 
        : 'text-muted-foreground text-xs font-normal';

    // Calculate bar style
    // We have p1 and p2. We draw from min(p1,p2) to max(p1,p2).
    const p1 = percentage;
    const p2 = idealPercentage;
    const styleLeft = Math.min(p1, p2);
    const styleWidth = Math.abs(p1 - p2);

    const outerClass = `flex flex-col w-full ${isDense ? 'gap-0 min-w-0' : isCompact ? 'gap-0.5 min-w-0' : 'gap-1 min-w-[140px]'}`;
    const valueClass = `flex justify-center font-bold ${isDense ? 'text-[10px]' : isCompact ? 'text-xs' : 'text-sm'}`;
    const rowGapClass = isDense ? 'gap-1' : isCompact ? 'gap-1.5' : 'gap-2';
    const labelWidthClass = isDense ? 'min-w-[32px]' : isCompact ? 'min-w-[38px]' : 'min-w-[48px]';
    const barHeightClass = isDense ? 'h-1.5' : isCompact ? 'h-2' : 'h-3';

    return (
      <div className={outerClass}>
        {/* Top: Numeric Value */}
        <div className={valueClass} style={{ color: currentColor }}>
            {formattedValue}
        </div>

        {/* Bottom: Bar with Labels */}
        <div className={`flex items-center ${rowGapClass}`}>
            <span 
                className={`font-medium ${labelWidthClass} text-right transition-all ${isLeftActive ? '' : inactiveLabelClass}`}
                style={isLeftActive ? activeStyle : undefined}
            >
                {leftLabel}
            </span>
            
            <div className={`relative ${barHeightClass} bg-muted rounded-full overflow-hidden flex-1`}>
                {/* Ideal Marker */}
                <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-background/80 z-10"
                    style={{ left: `${idealPercentage}%` }}
                />
                
                {/* Value Bar */}
                <div 
                    className="absolute top-0 bottom-0 transition-all duration-500"
                    style={{ 
                    left: `${styleLeft}%`,
                    width: `${styleWidth}%`,
                    backgroundColor: currentColor
                    }}
                />
            </div>

            <span 
                className={`font-medium ${labelWidthClass} text-left transition-all ${isRightActive ? '' : inactiveLabelClass}`}
                style={isRightActive ? activeStyle : undefined}
            >
                {rightLabel}
            </span>
        </div>
      </div>
    );
  }
  
  return null; // Circular not implemented
};

export default MetricGauge;